<template>
  <div class="pdp-summary-wrapper">
    <v-data-table
      :headers="headers"
      :items="summaryRecords"
      :items-per-page="-1"
      hide-default-footer
      dense
      class="pdp-summary-table"
      :item-class="() => 'table-row'"
    >
      <template v-slot:[`item.dateTime`]="{ item }">
        <div class="date-time-cell">
          <div class="date-time-content">
            <div class="status-label">{{ getMeetingStatus(item.End_Time || item.Date_Time) }}</div>
            <div class="date-text">{{ formatDate(item.Date_Time) }}</div>
          </div>
        </div>
      </template>

      <template v-slot:[`item.period`]="{ item }">
        <div class="period-cell">
          <span v-if="getPeriodDisplay(item)" class="period-text">{{ getPeriodDisplay(item) }}</span>
          <span v-else class="grey--text caption">—</span>
        </div>
      </template>

      <template v-slot:[`item.salary`]="{ item }">
        <div class="salary-cell">
          <div v-if="item.Summary_ID" class="salary-content">
            <div class="salary-item">
              <span class="salary-label">Previous:</span>
              <span class="salary-value">${{ getDisplayPreviousSalary(item) }}</span>
            </div>
            <div class="salary-item">
              <span class="salary-label">New:</span>
              <span class="salary-value">${{ item.New_Salary || 0 }}</span>
            </div>
          </div>
          <div v-else class="salary-content">
            <div class="salary-item grey--text">No summary</div>
          </div>
          <div class="salary-actions">
            <v-icon
              v-if="canEditSummary(item) && item.Summary_ID"
              small
              class="ml-2 grey--text action-icon"
              @click="editSummary(item)"
            >
              mdi-pencil-outline
            </v-icon>
            <v-btn
              v-if="canEditSummary(item) && !item.Summary_ID"
              x-small
              icon
              class="ml-2"
              @click="addSummaryForMeeting(item)"
            >
              <v-icon x-small>mdi-plus</v-icon>
            </v-btn>
          </div>
        </div>
      </template>

      <template v-slot:[`item.changes`]="{ item }">
        <div class="changes-cell">
          <div class="changes-content">
            <div v-if="item.changes && item.changes.length > 0" class="changes-list">
              <div
                v-for="change in item.changes"
                :key="change.Change_ID"
                class="change-item"
              >
                <div class="change-date">{{ getChangeDateFormatted(change) }}</div>
                <v-chip
                  small
                  class="change-chip mr-1 mb-1"
                  color="grey lighten-1"
                  :close="canEditSummary(item)"
                  @click:close="removeChange(item, change)"
                >
                  {{ change.Change_Type }}: {{ change.From_Value }} → {{ change.To_Value }}
                </v-chip>
              </div>
            </div>
          </div>
          <v-btn
            v-if="canEditSummary(item)"
            x-small
            icon
            class="add-change-btn"
            @click="showAddChangeDialog(item)"
          >
            <v-icon x-small>mdi-plus</v-icon>
          </v-btn>
        </div>
      </template>

      <template v-slot:[`item.notes`]="{ item }">
        <div class="notes-cell">
          <div class="notes-content">
            <div v-if="item.salaryNotes && item.salaryNotes.length > 0" class="notes-group mb-2">
              <div class="notes-category">Salary Notes:</div>
              <div class="notes-list">
                <span
                  v-for="note in item.salaryNotes"
                  :key="note.Note_ID"
                  class="note-comment"
                  :class="{ 'note-comment--long': isNoteLong(note.Note_Text) }"
                  @click="isNoteLong(note.Note_Text) && openFullNoteModal(note, 'Salary Notes')"
                >
                  {{ truncateNote(note.Note_Text) }}
                  <v-icon
                    v-if="canEditSummary(item)"
                    x-small
                    class="note-comment__delete"
                    @click.stop="deleteNote(item, note)"
                  >
                    mdi-close
                  </v-icon>
                </span>
              </div>
            </div>
            <div v-if="item.otherNotes && item.otherNotes.length > 0" class="notes-group mb-2">
              <div class="notes-category">Other Notes:</div>
              <div class="notes-list">
                <span
                  v-for="note in item.otherNotes"
                  :key="note.Note_ID"
                  class="note-comment"
                  :class="{ 'note-comment--long': isNoteLong(note.Note_Text) }"
                  @click="isNoteLong(note.Note_Text) && openFullNoteModal(note, 'Other Notes')"
                >
                  {{ truncateNote(note.Note_Text) }}
                  <v-icon
                    v-if="canEditSummary(item)"
                    x-small
                    class="note-comment__delete"
                    @click.stop="deleteNote(item, note)"
                  >
                    mdi-close
                  </v-icon>
                </span>
              </div>
            </div>
          </div>
          <v-btn
            v-if="canEditSummary(item)"
            x-small
            icon
            class="add-note-btn"
            @click="showAddNoteDialog(item)"
          >
            <v-icon x-small>mdi-plus</v-icon>
          </v-btn>
        </div>
      </template>

      <template v-slot:[`item.hrSupport`]="{ item }">
        <div class="hr-support-cell">
          <v-select
            v-if="canEditSummary(item)"
            v-model="item.HR_Support"
            :items="hrSupportOptions"
            dense
            flat
            hide-details
            placeholder="HR"
            class="hr-support-select"
            @change="updateSummary(item)"
          >
            <template v-slot:item="{ item }">
              <v-chip
                :color="item === 'Yes' ? 'yellow' : 'grey'"
                small
                text-color="black"
              >
                {{ item }}
              </v-chip>
            </template>
            <template v-slot:selection="{ item }">
              <v-chip
                :color="item === 'Yes' ? 'yellow' : 'grey'"
                small
                text-color="black"
              >
                {{ item }}
              </v-chip>
            </template>
          </v-select>
          <v-select
            v-else
            :color="item.HR_Support === 'Yes' ? 'yellow' : 'grey'"
            dense
            flat
            hide-details
            placeholder="HR"
            disabled
            small
            text-color="black"
            class="hr-support-select"
          >
            {{ item.HR_Support || 'No' }}
          </v-select>
        </div>
      </template>

      <template v-slot:[`item.hrSupportDetails`]="{ item }">
        <div class="hr-support-details-cell">
          <div class="hr-support-details-content">
            <div v-if="item.hrSupportDetails && item.hrSupportDetails.length > 0" class="hr-details-list">
              <div
                v-for="detail in item.hrSupportDetails"
                :key="detail.Detail_ID"
                class="hr-detail-card"
              >
                <div class="hr-detail-card-content">
                  <div class="hr-detail-header">
                    <v-chip
                      :color="detail.Status === 'Solved' ? 'green' : 'red'"
                      small
                      class="hr-status-chip"
                      text-color="white"
                    >
                      {{ detail.Status }}
                    </v-chip>
                    <div class="hr-detail-actions">
                      <v-menu
                        v-if="canEditSummary(item)"
                        offset-y
                        left
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            icon
                            x-small
                            class="hr-action-btn"
                            :class="detail.Reminder_Type ? 'primary--text' : 'grey--text'"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon small>mdi-bell{{ detail.Reminder_Type ? '' : '-outline' }}</v-icon>
                          </v-btn>
                        </template>
                        <v-list dense>
                          <v-list-item @click="setReminder(item, detail, 'Weekly')">
                            <v-list-item-title>Remind Weekly</v-list-item-title>
                          </v-list-item>
                          <v-list-item @click="setReminder(item, detail, 'Monthly')">
                            <v-list-item-title>Remind Monthly</v-list-item-title>
                          </v-list-item>
                          <v-list-item
                            v-if="detail.Reminder_Type"
                            @click="clearReminder(item, detail)"
                          >
                            <v-list-item-title>Clear Reminder</v-list-item-title>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                      <v-btn
                        v-if="canEditSummary(item)"
                        icon
                        x-small
                        class="hr-action-btn grey--text"
                        @click="editHrSupportDetail(item, detail)"
                      >
                        <v-icon small>mdi-pencil-outline</v-icon>
                      </v-btn>
                      <v-btn
                        v-if="canEditSummary(item)"
                        icon
                        x-small
                        class="hr-action-btn error--text"
                        @click="deleteHrSupportDetail(item, detail)"
                      >
                        <v-icon small>mdi-delete-outline</v-icon>
                      </v-btn>
                    </div>
                  </div>
                  <div
                    v-if="detail.Note_Text"
                    class="hr-detail-note"
                    :class="{ 'hr-detail-note--long': isNoteLong(detail.Note_Text) }"
                    @click="isNoteLong(detail.Note_Text) && openFullNoteModal({ Note_Text: detail.Note_Text }, 'HR Support Details')"
                  >
                    {{ truncateNote(detail.Note_Text) }}
                  </div>
                  <div class="hr-detail-meta">
                    <div v-if="detail.Due_Date" class="hr-detail-date">
                      <v-icon x-small class="mr-1">mdi-calendar</v-icon>
                      Due {{ formatDate(detail.Due_Date) }}
                    </div>
                    <div v-if="detail.Reminder_Type" class="hr-detail-reminder">
                      <v-icon x-small class="mr-1">mdi-bell</v-icon>
                      Reminder: {{ detail.Reminder_Type }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <v-btn
            v-if="canEditSummary(item)"
            x-small
            icon
            class="add-hr-detail-btn"
            @click="showAddHrSupportDetailDialog(item)"
          >
            <v-icon x-small>mdi-plus</v-icon>
          </v-btn>
        </div>
      </template>
    </v-data-table>

    <!-- Add Change Dialog -->
    <v-dialog v-model="showChangeDialog" max-width="600">
      <v-card>
        <v-card-title>Add Change</v-card-title>
        <v-card-text>
          <v-select
            v-model="newChange.Change_Type"
            :items="changeTypes"
            label="Change Type"
            solo
            dense
            @change="onChangeTypeChange"
          ></v-select>
          <v-select
            v-model="newChange.From_Value"
            :items="fromOptions"
            label="From"
            solo
            dense
          ></v-select>
          <v-select
            v-model="newChange.To_Value"
            :items="toOptions"
            label="To"
            solo
            dense
          ></v-select>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showChangeDialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="addChange">Add</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showFullNoteDialog" max-width="500" @click:outside="showFullNoteDialog = false">
      <v-card>
        <v-card-title class="text-subtitle-1">
          Comment{{ fullNoteCategory ? ` (${fullNoteCategory})` : '' }}
        </v-card-title>
        <v-card-text class="full-note-text">{{ fullNoteText }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showFullNoteDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add Note Dialog -->
    <v-dialog v-model="showNoteDialog" max-width="500">
      <v-card>
        <v-card-title>Add Note</v-card-title>
        <v-card-text>
          <v-select
            v-model="newNote.Note_Type"
            :items="noteTypes"
            label="Note Type"
            solo
            dense
          ></v-select>
          <v-textarea
            v-model="newNote.Note_Text"
            label="Note"
            rows="3"
            solo
            dense
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showNoteDialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="addNote">Add</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- HR Support Detail Dialog -->
    <v-dialog v-model="showHrSupportDialog" max-width="600">
      <v-card>
        <v-card-title>{{ editingHrSupportDetail ? 'Edit HR Support Detail' : 'Add HR Support Detail' }}</v-card-title>
        <v-card-text>
          <v-select
            v-model="hrSupportDetailForm.status"
            :items="['Solved', 'Unsolved']"
            label="Status"
            solo
            dense
          ></v-select>
          <v-textarea
            v-model="hrSupportDetailForm.noteText"
            label="Note"
            rows="3"
            solo
            dense
          ></v-textarea>
          <v-menu
            v-model="dueDateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="hrSupportDetailForm.dueDate"
                label="Due Date"
                prepend-icon="mdi-calendar"
                readonly
                solo
                dense
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="hrSupportDetailForm.dueDate"
              @input="dueDateMenu = false"
            ></v-date-picker>
          </v-menu>
          <v-select
            v-model="hrSupportDetailForm.reminderType"
            :items="reminderTypes"
            label="Reminder Type"
            solo
            dense
          >
            <template v-slot:item="{ item }">
              <v-list-item-title>{{ item === 'Remind Weekly' ? 'Weekly' : item === 'Remind Monthly' ? 'Monthly' : item }}</v-list-item-title>
            </template>
            <template v-slot:selection="{ item }">
              {{ item === 'Remind Weekly' ? 'Weekly' : item === 'Remind Monthly' ? 'Monthly' : item }}
            </template>
          </v-select>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeHrSupportDialog">Cancel</v-btn>
          <v-btn color="primary" @click="saveHrSupportDetail">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Summary Dialog -->
    <v-dialog v-model="showSummaryDialog" max-width="600">
      <v-card>
        <v-card-title>{{ editingSummary ? 'Edit Salary' : 'Add Salary' }}</v-card-title>
        <v-card-text>
          <v-text-field
            v-model.number="summaryForm.previousSalary"
            label="Previous Salary"
            type="number"
            solo
            dense
          ></v-text-field>
          <v-text-field
            v-model.number="summaryForm.newSalary"
            label="New Salary"
            type="number"
            solo
            dense
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeSummaryDialog">Cancel</v-btn>
          <v-btn color="primary" @click="saveSummary">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete HR Support Detail Dialog -->
    <ConfirmOrDeniedDialog
      v-if="showDeleteHrSupportDialog"
      v-model="showDeleteHrSupportDialog"
      :mode="confirmOrDeniedDialogModes.delete"
      :message="'Are you sure you want to delete this HR support detail?'"
      @onDeleteClick="confirmDeleteHrSupportDetail"
      @onCancelClick="showDeleteHrSupportDialog = false"
      @onCloseClick="showDeleteHrSupportDialog = false"
    />
  </div>
</template>

<script>
export default {
  name: 'PdpSummaryTab',
  components: {
    ConfirmOrDeniedDialog: () => import('@/components/global/ConfirmOrDeniedDialog.vue')
  },
  props: {
    employee: {
      required: true,
      type: Object
    },
    rolesOfUser: {
      required: false,
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      headers: [
        { text: 'Date & Time', value: 'dateTime', sortable: false, width: '100px', align: 'center' },
        { text: 'Period', value: 'period', sortable: false, width: '110px', align: 'center' },
        { text: 'Salary (Rate)', value: 'salary', sortable: false, width: '120px', align: 'center' },
        { text: 'Changes', value: 'changes', sortable: false, width: '300px', align: 'center' },
        { text: 'Notes', value: 'notes', sortable: false, align: 'center' },
        { text: 'HR Support', value: 'hrSupport', sortable: false, width: '50px', align: 'center' },
        { text: 'HR Support Details', value: 'hrSupportDetails', sortable: false, align: 'center' }
      ],
      summaryRecords: [],
      showChangeDialog: false,
      showNoteDialog: false,
      showHrSupportDialog: false,
      showSummaryDialog: false,
      showDeleteHrSupportDialog: false,
      deleteHrSupportDetailData: null,
      dueDateMenu: false,
      currentSummary: null,
      editingSummary: null,
      editingHrSupportDetail: null,
      newChange: {
        Change_Type: '',
        From_Value: '',
        To_Value: ''
      },
      newNote: {
        Note_Type: '',
        Note_Text: ''
      },
      hrSupportDetailForm: {
        status: 'Unsolved',
        noteText: '',
        dueDate: null,
        reminderType: null
      },
      summaryForm: {
        previousSalary: 0,
        newSalary: 0
      },
      changeTypes: ['Position Transfer', 'Unit', 'Supervisor'],
      noteTypes: ['Salary Notes', 'Other Notes'],
      hrSupportOptions: ['No', 'Yes'],
      reminderTypes: ['Remind Weekly', 'Remind Monthly'],
      reminderMenuOpen: false,
      fromOptions: [],
      toOptions: [],
      showFullNoteDialog: false,
      fullNoteText: '',
      fullNoteCategory: '',
      noteTruncateLength: 50
    }
  },
  computed: {
    user () {
      return this.$store.getters.getUser
    },
    isHRorFinancierOrManager () {
      return this.rolesOfUser.some(role => role.RoleName === 'HR' || role.RoleName === 'financier' || role.RoleName === 'manager')
    },
    isCurrentEmployee () {
      return this.employee && this.user && this.employee.Employee_ID === this.user.employee_id
    },
    employeeCurrentSalary () {
      if (!this.employee?.Employee_Rate?.length) return 0
      const rate = this.employee.Employee_Rate[this.employee.Employee_Rate.length - 1]
      return rate.Salary_Type === 'RATE' ? (rate.Rate ?? 0) : (rate.Salary ?? 0)
    },
    confirmOrDeniedDialogModes () {
      return this.$store.getters.getConfirmOrDeniedDialogModes
    }
  },
  async mounted () {
    if (this.employee && this.employee.Employee_ID) {
      await this.loadEmployeeRate()
      await this.loadSummaryRecords()
    }
  },
  methods: {
    canEditSummary (summary) {
      if (!summary || !summary.Date_Time) return false
      return this.isHRorFinancierOrManager
    },
    getMeetingStatus (dateString) {
      if (!dateString) return ''
      const meetingDate = new Date(dateString)
      const now = new Date()
      return meetingDate >= now ? 'Upcoming' : 'Past'
    },
    async loadEmployeeRate () {
      const res = await this.axios.get(`/api/employee_rate/${this.employee.Employee_ID}`, {
        headers: { Authorization: 'Bearer ' + this.user.token }
      })
      const rate = Array.isArray(res.data) ? res.data : (res.data ? [res.data] : [])
      this.$store.dispatch('setEmployeeRate', { rate, employeeId: this.employee.Employee_ID })
    },
    getDisplayPreviousSalary (item) {
      if (!item) return 0
      return item.Previous_Salary ?? 0
    },
    async loadSummaryRecords () {
      try {
        const response = await this.axios.get(`/api/pdp/summary/${this.employee.Employee_ID}`, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        this.summaryRecords = response.data

        for (const record of this.summaryRecords) {
          if (record.Summary_ID) {
            await this.loadSummaryData(record)
          }
        }
      } catch (error) {
        this.$store.dispatch('setError', { errorMsg: error.message })
      }
    },
    getPreviousPeriodNewSalary (currentMeetingDate) {
      if (!currentMeetingDate || !this.summaryRecords.length) return null
      const current = new Date(currentMeetingDate).getTime()
      const withSummary = this.summaryRecords
        .filter(r => r.Summary_ID && r.New_Salary != null && new Date(r.Date_Time).getTime() < current)
      if (!withSummary.length) return null
      const previous = withSummary.sort((a, b) => new Date(b.Date_Time).getTime() - new Date(a.Date_Time).getTime())[0]
      return previous.New_Salary
    },
    addSummaryForMeeting (meeting) {
      this.currentSummary = {
        Meeting_ID: meeting.Meeting_ID_From_Meeting || meeting.Meeting_ID,
        Employee_ID: this.employee.Employee_ID,
        Previous_Salary: 0,
        New_Salary: 0,
        HR_Support: 'No'
      }
      this.editingSummary = null
      const previousPeriodNew = this.getPreviousPeriodNewSalary(meeting.Date_Time)
      this.summaryForm = {
        previousSalary: previousPeriodNew != null ? previousPeriodNew : this.employeeCurrentSalary,
        newSalary: 0
      }
      this.showSummaryDialog = true
    },
    async loadSummaryData (record) {
      try {
        const [changesRes, notesRes, hrSupportRes] = await Promise.all([
          this.axios.get(`/api/pdp/summary/${record.Summary_ID}/changes`, {
            headers: { Authorization: 'Bearer ' + this.user.token }
          }),
          this.axios.get(`/api/pdp/summary/${record.Summary_ID}/notes`, {
            headers: { Authorization: 'Bearer ' + this.user.token }
          }),
          this.axios.get(`/api/pdp/summary/${record.Summary_ID}/hr-support`, {
            headers: { Authorization: 'Bearer ' + this.user.token }
          })
        ])

        this.$set(record, 'changes', changesRes.data)
        const notes = notesRes.data
        this.$set(record, 'salaryNotes', notes.filter(n => n.Note_Type === 'Salary Notes'))
        this.$set(record, 'otherNotes', notes.filter(n => n.Note_Type === 'Other Notes'))
        this.$set(record, 'hrSupportDetails', hrSupportRes.data)
      } catch (error) {
        this.$set(record, 'changes', [])
        this.$set(record, 'salaryNotes', [])
        this.$set(record, 'otherNotes', [])
        this.$set(record, 'hrSupportDetails', [])
      }
    },
    formatDate (dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
    },
    formatDateTime (dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${day}/${month}/${year} ${hours}:${minutes}`
    },
    formatPeriod (startDate, endDate) {
      if (!startDate) return ''
      const start = new Date(startDate)
      const end = endDate ? new Date(endDate) : null
      const startDay = String(start.getDate()).padStart(2, '0')
      const startMonth = String(start.getMonth() + 1).padStart(2, '0')
      const startYear = start.getFullYear()
      let endStr = ''
      if (end) {
        const endDay = String(end.getDate()).padStart(2, '0')
        const endMonth = String(end.getMonth() + 1).padStart(2, '0')
        const endYear = end.getFullYear()
        endStr = ` - ${endDay}/${endMonth}/${endYear}`
      }
      return `${startDay}/${startMonth}/${startYear}${endStr}`
    },
    getChangeDate (change) {
      if (!change) return null
      const raw = change.Created_At ?? change.created_at ?? change.CREATED_AT ?? null
      if (raw != null) {
        const d = new Date(raw)
        if (!isNaN(d.getTime())) return raw
      }
      for (const key of Object.keys(change || {})) {
        if (/created|date|time/i.test(key)) {
          const val = change[key]
          if (val != null && !isNaN(new Date(val).getTime())) return val
        }
      }
      return null
    },
    getChangeDateFormatted (change) {
      const raw = this.getChangeDate(change)
      return raw ? this.formatDate(raw) : '—'
    },
    getPeriodDisplay (item) {
      const start = item.Period_Start_Date || item.period_start_date
      const end = item.Period_End_Date || item.period_end_date
      if (start) return this.formatPeriod(start, end)
      if (!item.Date_Time) return ''
      const meetingDate = new Date(item.Date_Time)
      const periodStart = new Date(meetingDate.getFullYear(), meetingDate.getMonth() + 1, 1)
      const periodEnd = new Date(periodStart.getFullYear(), periodStart.getMonth() + 6, 0)
      return this.formatPeriod(periodStart, periodEnd)
    },
    async updateSummary (record) {
      try {
        if (!record.Summary_ID) {
          await this.axios.post('/api/pdp/summary', {
            meetingId: record.Meeting_ID_From_Meeting || record.Meeting_ID,
            employeeId: this.employee.Employee_ID,
            previousSalary: 0,
            newSalary: 0,
            hrSupport: record.HR_Support || 'No'
          }, {
            headers: {
              Authorization: 'Bearer ' + this.user.token
            }
          })
          await this.loadSummaryRecords()
          this.$store.dispatch('setSuccess', { successMsg: 'HR Support updated successfully' })
        } else {
          await this.axios.put(`/api/pdp/summary/${record.Summary_ID}`, {
            previousSalary: record.Previous_Salary || 0,
            newSalary: record.New_Salary || 0,
            hrSupport: record.HR_Support
          }, {
            headers: {
              Authorization: 'Bearer ' + this.user.token
            }
          })
          await this.loadSummaryRecords()
          this.$store.dispatch('setSuccess', { successMsg: 'HR Support updated successfully' })
        }
      } catch (error) {
        this.$store.dispatch('setError', { errorMsg: error.message })
      }
    },
    showAddChangeDialog (record) {
      this.currentSummary = record
      this.newChange = { Change_Type: '', From_Value: '', To_Value: '' }
      this.fromOptions = []
      this.toOptions = []
      this.showChangeDialog = true
    },
    async onChangeTypeChange () {
      if (!this.newChange.Change_Type) {
        this.fromOptions = []
        this.toOptions = []
        return
      }

      try {
        if (this.newChange.Change_Type === 'Position Transfer') {
          const statusResponse = await this.axios.get('/api/employee_status', {
            headers: {
              Authorization: 'Bearer ' + this.user.token
            }
          })
          const positions = statusResponse.data || []

          const currentPosition = this.employee.Status || ''
          this.fromOptions = currentPosition ? [currentPosition] : []

          this.toOptions = positions
            .filter(pos => pos.Status_Descr && pos.Status_Descr !== currentPosition)
            .map(pos => pos.Status_Descr)
        } else if (this.newChange.Change_Type === 'Supervisor') {
          const employeesResponse = await this.axios.get('/api/employeelist', {
            headers: {
              Authorization: 'Bearer ' + this.user.token
            }
          })
          const employees = employeesResponse.data || []

          const currentSupervisorId = this.employee.Supervisor_ID
          const currentSupervisorName = this.employee.Supervisor_Full_Name
          let currentSupervisor = null

          if (currentSupervisorId) {
            currentSupervisor = employees.find(emp => emp.employee_id === currentSupervisorId)
          }

          if (!currentSupervisor && currentSupervisorName && currentSupervisorName !== '[ ]') {
            currentSupervisor = employees.find(emp => emp.full_name === currentSupervisorName)
          }

          this.fromOptions = currentSupervisor ? [currentSupervisor.full_name] : (currentSupervisorName && currentSupervisorName !== '[ ]' ? [currentSupervisorName] : [])

          this.toOptions = employees
            .filter(emp => emp.employee_id !== currentSupervisorId)
            .map(emp => emp.full_name)
        } else if (this.newChange.Change_Type === 'Unit') {
          const deptResponse = await this.axios.get('/api/department', {
            headers: {
              Authorization: 'Bearer ' + this.user.token
            }
          })
          const departments = deptResponse.data || []

          const currentDepartment = this.employee.Department || ''
          this.fromOptions = currentDepartment ? [currentDepartment] : []

          this.toOptions = departments
            .filter(dept => dept.Dep_Name && dept.Dep_Name !== currentDepartment && dept.Is_Active)
            .map(dept => dept.Dep_Name)
        }
      } catch (error) {
        console.error('Error loading options:', error)
        this.fromOptions = []
        this.toOptions = []
      }
    },
    async addChange () {
      if (!this.newChange.Change_Type || !this.newChange.From_Value || !this.newChange.To_Value) return

      try {
        await this.axios.post(`/api/pdp/summary/${this.currentSummary.Summary_ID}/changes`, {
          changeType: this.newChange.Change_Type,
          fromValue: this.newChange.From_Value,
          toValue: this.newChange.To_Value
        }, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        await this.loadSummaryData(this.currentSummary)
        this.showChangeDialog = false
        this.$store.dispatch('setSuccess', { successMsg: 'Change added successfully' })
      } catch (error) {
        this.$store.dispatch('setError', { errorMsg: error.message })
      }
    },
    async removeChange (record, change) {
      try {
        await this.axios.delete(`/api/pdp/summary/changes/${change.Change_ID}`, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        await this.loadSummaryData(record)
        this.$store.dispatch('setSuccess', { successMsg: 'Change removed successfully' })
      } catch (error) {
        this.$store.dispatch('setError', { errorMsg: error.message })
      }
    },
    showAddNoteDialog (record) {
      this.currentSummary = record
      this.newNote = { Note_Type: '', Note_Text: '' }
      this.showNoteDialog = true
    },
    async addNote () {
      if (!this.newNote.Note_Type || !this.newNote.Note_Text.trim()) return

      try {
        await this.axios.post(`/api/pdp/summary/${this.currentSummary.Summary_ID}/notes`, {
          noteType: this.newNote.Note_Type,
          noteText: this.newNote.Note_Text
        }, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        await this.loadSummaryData(this.currentSummary)
        this.showNoteDialog = false
        this.$store.dispatch('setSuccess', { successMsg: 'Note added successfully' })
      } catch (error) {
        this.$store.dispatch('setError', { errorMsg: error.message })
      }
    },
    async deleteNote (record, note) {
      try {
        await this.axios.delete(`/api/pdp/summary/notes/${note.Note_ID}`, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        await this.loadSummaryData(record)
        this.$store.dispatch('setSuccess', { successMsg: 'Note deleted successfully' })
      } catch (error) {
        this.$store.dispatch('setError', { errorMsg: error.message })
      }
    },
    truncateNote (text) {
      if (!text || typeof text !== 'string') return ''
      if (text.length <= this.noteTruncateLength) return text
      return text.slice(0, this.noteTruncateLength).trim() + '...'
    },
    isNoteLong (text) {
      return text && typeof text === 'string' && text.length > this.noteTruncateLength
    },
    openFullNoteModal (note, category) {
      this.fullNoteText = note.Note_Text || ''
      this.fullNoteCategory = category || ''
      this.showFullNoteDialog = true
    },
    showAddHrSupportDetailDialog (record) {
      this.currentSummary = record
      this.hrSupportDetailForm = {
        status: 'Unsolved',
        noteText: '',
        dueDate: null,
        reminderType: null
      }
      this.showHrSupportDialog = true
    },
    editHrSupportDetail (record, detail) {
      this.currentSummary = record
      this.editingHrSupportDetail = detail.Detail_ID
      this.hrSupportDetailForm = {
        status: detail.Status,
        noteText: detail.Note_Text || '',
        dueDate: detail.Due_Date ? new Date(detail.Due_Date).toISOString().split('T')[0] : null,
        reminderType: detail.Reminder_Type ? (detail.Reminder_Type === 'Weekly' ? 'Remind Weekly' : detail.Reminder_Type === 'Monthly' ? 'Remind Monthly' : detail.Reminder_Type) : null
      }
      this.showHrSupportDialog = true
    },
    deleteHrSupportDetail (record, detail) {
      this.deleteHrSupportDetailData = { record, detail }
      this.showDeleteHrSupportDialog = true
    },
    async confirmDeleteHrSupportDetail () {
      if (!this.deleteHrSupportDetailData) return
      const { record, detail } = this.deleteHrSupportDetailData
      try {
        await this.axios.delete(`/api/pdp/summary/hr-support/${detail.Detail_ID}`, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        await this.loadSummaryData(record)
        this.$store.dispatch('setSuccess', { successMsg: 'HR support detail deleted successfully' })
        this.deleteHrSupportDetailData = null
      } catch (error) {
        this.$store.dispatch('setError', { errorMsg: error.message })
      }
    },
    closeHrSupportDialog () {
      this.showHrSupportDialog = false
      this.editingHrSupportDetail = null
      this.hrSupportDetailForm = {
        status: 'Unsolved',
        noteText: '',
        dueDate: null,
        reminderType: null
      }
    },
    async saveHrSupportDetail () {
      try {
        const reminderType = this.hrSupportDetailForm.status === 'Solved'
          ? null
          : (this.hrSupportDetailForm.reminderType === 'Remind Weekly' ? 'Weekly' : this.hrSupportDetailForm.reminderType === 'Remind Monthly' ? 'Monthly' : this.hrSupportDetailForm.reminderType)
        if (this.editingHrSupportDetail) {
          await this.axios.put(`/api/pdp/summary/hr-support/${this.editingHrSupportDetail}`, {
            status: this.hrSupportDetailForm.status,
            noteText: this.hrSupportDetailForm.noteText,
            dueDate: this.hrSupportDetailForm.dueDate,
            reminderType: reminderType
          }, {
            headers: {
              Authorization: 'Bearer ' + this.user.token
            }
          })
          this.$store.dispatch('setSuccess', { successMsg: 'HR support detail updated successfully' })
        } else {
          await this.axios.post(`/api/pdp/summary/${this.currentSummary.Summary_ID}/hr-support`, {
            status: this.hrSupportDetailForm.status,
            noteText: this.hrSupportDetailForm.noteText,
            dueDate: this.hrSupportDetailForm.dueDate,
            reminderType: reminderType
          }, {
            headers: {
              Authorization: 'Bearer ' + this.user.token
            }
          })
          this.$store.dispatch('setSuccess', { successMsg: 'HR support detail added successfully' })
        }
        await this.loadSummaryData(this.currentSummary)
        this.closeHrSupportDialog()
      } catch (error) {
        this.$store.dispatch('setError', { errorMsg: error.message })
      }
    },
    async setReminder (record, detail, reminderType) {
      try {
        await this.axios.put(`/api/pdp/summary/hr-support/${detail.Detail_ID}`, {
          status: detail.Status,
          noteText: detail.Note_Text || '',
          dueDate: detail.Due_Date || null,
          reminderType: reminderType
        }, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        await this.loadSummaryData(record)
        this.$store.dispatch('setSuccess', { successMsg: `Reminder set to ${reminderType}` })
      } catch (error) {
        this.$store.dispatch('setError', { errorMsg: error.message })
      }
    },
    async clearReminder (record, detail) {
      try {
        await this.axios.put(`/api/pdp/summary/hr-support/${detail.Detail_ID}`, {
          status: detail.Status,
          noteText: detail.Note_Text || '',
          dueDate: detail.Due_Date || null,
          reminderType: null
        }, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        await this.loadSummaryData(record)
        this.$store.dispatch('setSuccess', { successMsg: 'Reminder cleared successfully' })
      } catch (error) {
        this.$store.dispatch('setError', { errorMsg: error.message })
      }
    },
    editSummary (record) {
      this.currentSummary = record
      this.editingSummary = record
      this.summaryForm = {
        previousSalary: record.Previous_Salary || 0,
        newSalary: record.New_Salary || 0
      }
      this.showSummaryDialog = true
    },
    closeSummaryDialog () {
      this.showSummaryDialog = false
      this.currentSummary = null
      this.editingSummary = null
      this.summaryForm = {
        previousSalary: 0,
        newSalary: 0,
        hrSupport: 'No'
      }
    },
    async saveSummary () {
      try {
        if (this.editingSummary) {
          await this.axios.put(`/api/pdp/summary/${this.editingSummary.Summary_ID}`, {
            previousSalary: this.summaryForm.previousSalary,
            newSalary: this.summaryForm.newSalary,
            hrSupport: this.editingSummary.HR_Support || 'No'
          }, {
            headers: {
              Authorization: 'Bearer ' + this.user.token
            }
          })
          this.$store.dispatch('setSuccess', { successMsg: 'Salary updated successfully' })
        } else {
          await this.axios.post('/api/pdp/summary', {
            meetingId: this.currentSummary.Meeting_ID,
            employeeId: this.currentSummary.Employee_ID,
            previousSalary: this.summaryForm.previousSalary,
            newSalary: this.summaryForm.newSalary,
            hrSupport: this.currentSummary.HR_Support || 'No'
          }, {
            headers: {
              Authorization: 'Bearer ' + this.user.token
            }
          })
          this.$store.dispatch('setSuccess', { successMsg: 'Salary added successfully' })
        }
        await this.loadSummaryRecords()
        this.closeSummaryDialog()
      } catch (error) {
        this.$store.dispatch('setError', { errorMsg: error.message })
      }
    },
    async deleteSummary (record) {
      if (!confirm('Are you sure you want to delete this summary record?')) return
      try {
        await this.axios.delete(`/api/pdp/summary/${record.Summary_ID}`, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        await this.loadSummaryRecords()
        this.$store.dispatch('setSuccess', { successMsg: 'Summary deleted successfully' })
      } catch (error) {
        this.$store.dispatch('setError', { errorMsg: error.message })
      }
    }
  }
}
</script>

<style scoped>
.pdp-summary-wrapper {
  padding: 0 16px 16px 16px;
}

.pdp-summary-table {
  background: var(--v-surface-base);
  border-radius: 4px;
}

.pdp-summary-table >>> .v-data-table__wrapper {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
}

.theme--dark .pdp-summary-table >>> .v-data-table__wrapper {
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.pdp-summary-table >>> table {
  border-collapse: collapse;
}

.pdp-summary-table >>> table tbody td {
  padding: 4px !important;
  border-right: 1px solid rgba(0, 0, 0, 0.08) !important;
}

.pdp-summary-table >>> table tbody td:has(.hr-support-cell) {
  padding: 0 !important;
}

.theme--dark .pdp-summary-table >>> table tbody td {
  border-right: 1px solid rgba(255, 255, 255, 0.08) !important;
}

.pdp-summary-table >>> table tbody td:last-child {
  border-right: none !important;
}

.pdp-summary-table >>> .v-input__append-inner {
  padding: 0 !important;
}

.pdp-summary-table >>> table thead th {
  border-right: 1px solid rgba(0, 0, 0, 0.12) !important;
}

.theme--dark .pdp-summary-table >>> table thead th {
  border-right: 1px solid rgba(255, 255, 255, 0.12) !important;
}

.pdp-summary-table >>> table thead th:last-child {
  border-right: none !important;
}

.table-row {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.theme--dark .table-row {
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.table-row:hover {
  background-color: rgba(var(--v-theme-on-surface-rgb, 0, 0, 0), 0.02);
}

.theme--dark .table-row:hover {
  background-color: rgba(var(--v-theme-on-surface-rgb, 255, 255, 255), 0.08);
}

.date-time-cell {
  display: flex;
  align-items: center;
  padding: 8px 0;
}

.date-time-content {
  display: flex;
  flex-direction: column;
}

.status-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: rgba(var(--v-theme-on-surface-rgb, 0, 0, 0), 0.6);
  margin-bottom: 4px;
}

.theme--dark .status-label {
  color: rgba(var(--v-theme-on-surface-rgb, 255, 255, 255), 0.7);
}

.date-text {
  font-size: 0.875rem;
  color: var(--v-theme-on-surface);
}

.action-icons {
  display: flex;
  align-items: center;
}

.action-icon {
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.2s;
}

.action-icon:hover {
  opacity: 1;
}

.salary-cell {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.salary-content {
  flex: 1;
}

.salary-actions {
  display: flex;
  align-items: center;
}

.salary-cell {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 0;
}

.salary-content {
  flex: 1;
}

.salary-actions {
  display: flex;
  align-items: center;
}

.salary-item {
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.salary-item:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.theme--dark .salary-item {
  border-bottom-color: rgba(255, 255, 255, 0.08);
}

.salary-label {
  font-size: 0.875rem;
  color: rgba(var(--v-theme-on-surface-rgb, 0, 0, 0), 0.6);
  margin-right: 8px;
}

.theme--dark .salary-label {
  color: rgba(var(--v-theme-on-surface-rgb, 255, 255, 255), 0.7);
}

.salary-value {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--v-theme-on-surface);
}

.hr-support-cell {
  display: flex;
  justify-content: center;
  padding: 8px 0;
}

.hr-support-cell .hr-support-select {
  width: fit-content !important;
  min-width: 0;
}

.hr-support-select >>> .v-input__slot,
.hr-support-select >>> .v-select__selections {
  display: flex !important;
  justify-content: center !important;
}

/* Прибираємо пустий input у v-select, щоб чіп можна було відцентрувати */
.hr-support-select >>> .v-select__selections input {
  width: 0 !important;
  min-width: 0 !important;
  flex: 0 0 0 !important;
  padding: 0 !important;
  overflow: hidden !important;
}

.changes-cell {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 2px 0;
}

.changes-content {
  flex: 1;
  min-width: 0;
}

.add-change-btn {
  align-self: center;
  margin-left: 8px;
}

.hr-support-details-cell {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 8px 0;
}

.hr-support-details-content {
  flex: 1;
  min-width: 0;
}

.notes-cell {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 0;
}

.notes-content {
  flex: 1;
  min-width: 0;
}

.add-note-btn {
  align-self: center;
  margin-left: 8px;
}

.changes-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 4px;
  min-width: 0;
  width: 100%;
}

.change-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.change-date {
  font-size: 0.7rem;
  color: rgba(var(--v-theme-on-surface-rgb, 0, 0, 0), 0.55);
}

.theme--dark .change-date {
  color: rgba(255, 255, 255, 0.55);
}

.period-cell {
  padding: 4px 0;
}

.period-cell .period-text {
  font-size: 0.875rem;
}

.change-chip {
  max-width: 100%;
  height: auto;
  white-space: normal;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.change-chip >>> .v-chip__content {
  white-space: normal;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.notes-group {
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.notes-group:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.theme--dark .notes-group {
  border-bottom-color: rgba(255, 255, 255, 0.08);
}

.notes-category {
  font-size: 0.75rem;
  font-weight: 600;
  color: rgba(var(--v-theme-on-surface-rgb, 0, 0, 0), 0.6);
  margin-bottom: 4px;
}

.theme--dark .notes-category {
  color: rgba(var(--v-theme-on-surface-rgb, 255, 255, 255), 0.7);
}

.notes-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  min-width: 0;
  width: 100%;
}

.note-comment {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.875rem;
  color: var(--v-theme-on-surface);
  padding: 2px 0;
  margin-right: 8px;
  margin-bottom: 4px;
  max-width: 100%;
  min-width: 0;
}

.note-comment__text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.note-comment--long {
  cursor: pointer;
}

.note-comment--long:hover {
  opacity: 0.85;
}

.note-comment__delete {
  cursor: pointer;
  opacity: 0.6;
}

.note-comment__delete:hover {
  opacity: 1;
}

.full-note-text {
  white-space: pre-wrap;
  word-break: break-word;
}

.hr-support-select {
  font-size: 0.875rem;
}

.hr-support-select >>> .v-input__control {
  min-height: 24px !important;
  height: 24px !important;
}

.hr-support-select >>> .v-input__slot {
  margin-bottom: 0 !important;
  padding: 0 2px !important;
  min-height: 24px !important;
  background: transparent !important;
  box-shadow: none !important;
  border: none !important;
}

.hr-support-select >>> .v-input__slot::before,
.hr-support-select >>> .v-input__slot::after {
  display: none !important;
}

.hr-support-select >>> .v-select__selections {
  padding-left: 0;
}

.hr-support-select >>> .v-select__selections .v-select__selection--comma {
  padding-left: 0;
}

.hr-details-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.hr-detail-card {
  padding: 8px 0;
  margin-bottom: 4px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.hr-detail-card:last-child {
  margin-bottom: 0;
  border-bottom: none;
  padding-bottom: 0;
}

.theme--dark .hr-detail-card {
  border-bottom-color: rgba(255, 255, 255, 0.08);
}

.theme--dark .hr-detail-card:last-child {
  border-bottom: none;
}

.hr-detail-card-content {
  padding: 0;
}

.hr-detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.hr-status-chip {
  font-weight: 500;
  height: 24px;
  min-width: 70px;
  justify-content: center;
}

.hr-detail-actions {
  display: flex;
  gap: 4px;
  align-items: center;
}

.hr-action-btn {
  width: 24px;
  height: 24px;
  min-width: 24px;
  padding: 0;
}

.hr-action-btn:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.theme--dark .hr-action-btn:hover {
  background-color: rgba(255, 255, 255, 0.08);
}

.hr-detail-note {
  font-size: 0.875rem;
  color: var(--v-theme-on-surface);
  line-height: 1.5;
  margin-bottom: 8px;
  word-wrap: break-word;
}

.hr-detail-note--long {
  cursor: pointer;
}

.hr-detail-note--long:hover {
  opacity: 0.85;
}

.hr-detail-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}

.theme--dark .hr-detail-meta {
  border-top: 1px solid rgba(255, 255, 255, 0.12);
}

.hr-detail-date {
  font-size: 0.75rem;
  color: rgba(var(--v-theme-on-surface-rgb, 0, 0, 0), 0.6);
  display: flex;
  align-items: center;
}

.theme--dark .hr-detail-date {
  color: rgba(var(--v-theme-on-surface-rgb, 255, 255, 255), 0.7);
}

.hr-detail-reminder {
  font-size: 0.75rem;
  color: var(--v-primary-base);
  font-weight: 500;
  display: flex;
  align-items: center;
}

.add-hr-detail-btn {
  align-self: center;
  margin-left: 8px;
}

.theme--dark .add-hr-detail-btn {
  background-color: rgba(var(--v-theme-on-surface-rgb, 255, 255, 255), 0.08);
}

.add-hr-detail-btn:hover {
  background-color: rgba(var(--v-theme-on-surface-rgb, 0, 0, 0), 0.1);
}

.theme--dark .add-hr-detail-btn:hover {
  background-color: rgba(var(--v-theme-on-surface-rgb, 255, 255, 255), 0.12);
}
</style>
